import React, { useState, useMemo } from "react";
import { 
  Search, MapPin, Briefcase, Building2, GraduationCap, BookOpen, 
  Layers, Sparkles, HelpCircle, BarChart3, Compass, 
  ArrowRight, ChevronRight, ShieldCheck, Filter, AlertTriangle
} from "lucide-react";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { GLOBAL_SEO_REGISTRY, SeoRegistryEntry } from "../../utils/seoRegistry";
import { 
  getAllQualifiedLocations, 
  getAllSupportedCountries
} from "../../utils/locationRegistry";

interface ExploreHubPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function ExploreHubPage({ onNavigate, onLogin = () => {} }: ExploreHubPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [hasError] = useState(false);

  useSeoMetadata({
    title: "Explore Local Jobs, Hiring Businesses & Career Resources | BizNearYou",
    description: "Explore local job opportunities, businesses hiring nearby, internships, industries, guides, reports, and career-search tools from BizNearYou.",
    canonicalUrl: "https://getbiznearyou.com/explore"
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  const qualifiedLocations = useMemo(() => {
    try {
      return getAllQualifiedLocations().slice(0, 8);
    } catch (err) {
      console.error("Failed to load qualified locations:", err);
      return [];
    }
  }, []);

  const supportedCountries = useMemo(() => {
    try {
      return getAllSupportedCountries();
    } catch (err) {
      console.error("Failed to load supported countries:", err);
      return [];
    }
  }, []);

  const allEntries: SeoRegistryEntry[] = useMemo(() => {
    try {
      return Object.values(GLOBAL_SEO_REGISTRY).filter(e => e.sitemapInclusion && e.publicationStatus === "published");
    } catch (err) {
      console.error("Failed to load SEO registry:", err);
      return [];
    }
  }, []);

  // Filter Search
  const filterCategoryMap: Record<string, string[]> = {
    all: ["core", "location-directory", "location", "industry-directory", "industry", "industry-location", "guide-hub", "guide", "feature-hub", "feature", "report-hub", "report", "methodology", "compare-hub", "compare"],
    locations: ["location-directory", "location"],
    guides: ["guide-hub", "guide"],
    industries: ["industry-directory", "industry", "industry-location"],
    features: ["feature-hub", "feature"],
    methodology: ["methodology"],
    reports: ["report-hub", "report"]
  };

  const filteredSearchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    try {
      const allowedTypes = filterCategoryMap[activeCategory] || filterCategoryMap.all;
      const q = searchQuery.toLowerCase();
      return allEntries.filter(entry => {
        if (!allowedTypes.includes(entry.pageType)) return false;
        return (
          entry.title.toLowerCase().includes(q) ||
          entry.h1.toLowerCase().includes(q) ||
          entry.metaDescription.toLowerCase().includes(q) ||
          entry.breadcrumbLabel.toLowerCase().includes(q) ||
          entry.route.toLowerCase().includes(q)
        );
      }).slice(0, 18);
    } catch (err) {
      console.error("Error filtering search results:", err);
      return [];
    }
  }, [searchQuery, activeCategory, allEntries]);

  if (hasError) {
    return (
      <div className="w-full bg-slate-50 dark:bg-slate-950 min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl shadow-md">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Explore Resources Temporarily Unavailable
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Explore resources are temporarily unavailable. Browse our Guides, Features, and Reports instead.
          </p>
          <div className="flex flex-wrap gap-2 justify-center pt-2">
            <a href="/guides" onClick={(e) => handleLinkClick(e, "/guides")} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold">Guides</a>
            <a href="/features" onClick={(e) => handleLinkClick(e, "/features")} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold">Features</a>
            <a href="/reports" onClick={(e) => handleLinkClick(e, "/reports")} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold">Reports</a>
            <a href="/locations" onClick={(e) => handleLinkClick(e, "/locations")} className="px-3 py-1.5 bg-slate-700 text-white rounded-lg text-xs font-bold">Locations</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 min-h-screen">
      <main className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        <SeoBreadcrumbs items={[{ label: "Explore", path: "/explore" }]} onNavigate={onNavigate} />

        {/* Header Section */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 space-y-4 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
              <Compass className="w-3.5 h-3.5" /> Central Directory Hub
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-tight">
              Explore BizNearYou
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Browse local job-search resources, hiring-location pages, industries, guides, reports, and BizNearYou features.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800 space-y-4">
            <div className="relative max-w-2xl">
              <Search className="w-5 h-5 text-slate-400 absolute left-4 top-3.5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources, cities, guides, industries, features..."
                className="w-full pl-12 pr-4 py-3 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
              />
            </div>

            {searchQuery.trim().length > 0 && (
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1 pr-2 shrink-0">
                    <Filter className="w-3.5 h-3.5" /> Filter:
                  </span>
                  {[
                    { id: "all", label: "All Types" },
                    { id: "locations", label: "Locations" },
                    { id: "guides", label: "Guides" },
                    { id: "industries", label: "Industries" },
                    { id: "features", label: "Features" },
                    { id: "reports", label: "Reports" }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveCategory(tab.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer whitespace-nowrap ${
                        activeCategory === tab.id
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 rounded-2xl p-6 space-y-3 shadow-xs">
                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800 pb-2">
                    <span>Search Results for "{searchQuery}"</span>
                    <span>{filteredSearchResults.length} pages found</span>
                  </div>

                  {filteredSearchResults.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-1">
                      {filteredSearchResults.map(entry => (
                        <a
                          key={entry.route}
                          href={entry.route}
                          onClick={(e) => handleLinkClick(e, entry.route)}
                          className="p-3.5 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-750 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500 transition cursor-pointer flex flex-col justify-between space-y-2 group"
                        >
                          <div>
                            <span className="text-[10px] uppercase tracking-wider font-bold text-blue-600 dark:text-blue-400 block mb-1">
                              {entry.pageType.replace("-", " ")}
                            </span>
                            <h4 className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-300 transition line-clamp-1">
                              {entry.title}
                            </h4>
                            <p className="text-[11px] text-slate-600 dark:text-slate-400 line-clamp-2 mt-1">
                              {entry.metaDescription}
                            </p>
                          </div>
                          <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200 flex items-center gap-1">
                            {entry.route} <ChevronRight className="w-3 h-3 text-blue-500 opacity-0 group-hover:opacity-100 transition" />
                          </span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400 py-4 text-center">
                      No matching public resources found for "{searchQuery}". Try searching for major cities like Peoria or Chicago, or guides on cold email.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Section 1: Explore by Location */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Explore by Location
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Discover verified local employers, hiring signals, and student opportunities across major countries and cities.
              </p>
            </div>
            <a
              href="/locations"
              onClick={(e) => handleLinkClick(e, "/locations")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 hover:bg-blue-100 font-bold text-xs transition shrink-0"
            >
              Main Location Directory <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Country Level Links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "United States", code: "US", flag: "🇺🇸", route: "/locations/united-states", desc: "180+ qualified US city hubs" },
              { name: "Canada", code: "CA", flag: "🇨🇦", route: "/locations/canada", desc: "100+ Canadian metropolitan markets" },
              { name: "Mexico", code: "MX", flag: "🇲🇽", route: "/locations/mexico", desc: "Key commercial hubs in Mexico" },
              { name: "International", code: "INTL", flag: "🌐", route: "/locations/international", desc: "UK, Europe & Australia cities" }
            ].map(c => (
              <a
                key={c.code}
                href={c.route}
                onClick={(e) => handleLinkClick(e, c.route)}
                className="p-4 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition flex items-start gap-3 group"
              >
                <span className="text-2xl">{c.flag}</span>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition">
                    {c.name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{c.desc}</p>
                </div>
              </a>
            ))}
          </div>

          {/* Featured Cities Grid */}
          <div className="pt-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
              Popular Local City Hubs
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { city: "Peoria, IL", jobs: "/jobs/peoria-il", hiring: "/businesses-hiring/peoria-il", internships: "/internships/peoria-il" },
                { city: "Chicago, IL", jobs: "/jobs/chicago-il", hiring: "/businesses-hiring/chicago-il", internships: "/internships/chicago-il" },
                { city: "Springfield, IL", jobs: "/jobs/springfield-il", hiring: "/businesses-hiring/springfield-il", internships: "/internships/springfield-il" },
                { city: "Austin, TX", jobs: "/jobs/austin-tx", hiring: "/businesses-hiring/austin-tx", internships: "/internships/austin-tx" }
              ].map(item => (
                <div key={item.city} className="bg-slate-50 dark:bg-slate-800/80 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 space-y-2">
                  <div className="flex items-center gap-1.5 font-bold text-sm text-slate-900 dark:text-white">
                    <MapPin className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                    <span>{item.city}</span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <a href={item.jobs} onClick={(e) => handleLinkClick(e, item.jobs)} className="block text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition">
                      • Jobs in {item.city}
                    </a>
                    <a href={item.hiring} onClick={(e) => handleLinkClick(e, item.hiring)} className="block text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition">
                      • Businesses Hiring Signals
                    </a>
                    <a href={item.internships} onClick={(e) => handleLinkClick(e, item.internships)} className="block text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition">
                      • Student Internships
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 2: Explore Job Opportunities */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Explore Job Opportunities
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                City-specific job search indexes, unadvertised positions, and entry-level career resources.
              </p>
            </div>
            <a
              href="/jobs/locations"
              onClick={(e) => handleLinkClick(e, "/jobs/locations")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 font-bold text-xs transition shrink-0"
            >
              Browse Jobs Directory <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
            {[
              { title: "Find Jobs Tool", route: "/find-jobs", desc: "Search local employers with open opportunities" },
              { title: "Hidden Jobs Guide", route: "/hidden-jobs", desc: "Strategies for finding unadvertised positions" },
              { title: "First Jobs", route: "/first-jobs", desc: "Entry-level jobs for recent graduates & teens" },
              { title: "City Job Directory", route: "/jobs/locations", desc: "Index of jobs organized by city" }
            ].map(item => (
              <a
                key={item.route}
                href={item.route}
                onClick={(e) => handleLinkClick(e, item.route)}
                className="p-4 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition flex flex-col justify-between space-y-2 group"
              >
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.desc}</p>
                </div>
                <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                  View <ChevronRight className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* Section 3: Explore Businesses Hiring */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Explore Businesses Hiring
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Cities and commercial areas grouped by review growth, expansion, and hiring signals.
              </p>
            </div>
            <a
              href="/businesses-hiring/locations"
              onClick={(e) => handleLinkClick(e, "/businesses-hiring/locations")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 font-bold text-xs transition shrink-0"
            >
              Browse Hiring Signals <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {[
              { title: "Businesses Hiring Near Me", route: "/businesses-hiring-near-me", desc: "Discover nearby commercial zones with strong growth signals" },
              { title: "Hiring Signals Explained", route: "/hiring-signals-explained", desc: "Learn how we detect local employer expansion before job ads go live" },
              { title: "Hiring Locations Directory", route: "/businesses-hiring/locations", desc: "Cities ranked by business density and hiring activity" }
            ].map(item => (
              <a
                key={item.route}
                href={item.route}
                onClick={(e) => handleLinkClick(e, item.route)}
                className="p-4 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition flex flex-col justify-between space-y-2 group"
              >
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.desc}</p>
                </div>
                <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  View <ChevronRight className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* Section 4: Explore Internships */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Explore Internships
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Local private practices, medical offices, and firms open to student outreach and internships.
              </p>
            </div>
            <a
              href="/internships/locations"
              onClick={(e) => handleLinkClick(e, "/internships/locations")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-violet-50 dark:bg-violet-950 text-violet-700 dark:text-violet-300 hover:bg-violet-100 font-bold text-xs transition shrink-0"
            >
              Browse Student Hub <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {[
              { title: "Local Internships Guide", route: "/local-internships", desc: "How students can pitch local business owners directly" },
              { title: "Internship Locations Index", route: "/internships/locations", desc: "Cities with high density of internship-friendly firms" },
              { title: "First Jobs", route: "/first-jobs", desc: "Entry-level opportunities for students and recent grads" }
            ].map(item => (
              <a
                key={item.route}
                href={item.route}
                onClick={(e) => handleLinkClick(e, item.route)}
                className="p-4 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition flex flex-col justify-between space-y-2 group"
              >
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-violet-600 dark:group-hover:text-violet-400 transition">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.desc}</p>
                </div>
                <span className="text-[11px] font-semibold text-violet-600 dark:text-violet-400 flex items-center gap-1">
                  View <ChevronRight className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* Section 5: Browse Guides */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Browse Guides
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Practical strategies for finding unadvertised jobs, contacting business owners, and pitching your skills.
              </p>
            </div>
            <a
              href="/guides"
              onClick={(e) => handleLinkClick(e, "/guides")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-50 dark:bg-amber-950 text-amber-800 dark:text-amber-300 hover:bg-amber-100 font-bold text-xs transition shrink-0"
            >
              All Strategy Guides <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {[
              { title: "How It Works", route: "/how-it-works", desc: "Step-by-step walkthrough of direct local business discovery" },
              { title: "Contacting Local Businesses", route: "/how-to-contact-local-businesses-for-a-job", desc: "Email and outreach scripts that get responses" },
              { title: "Prioritizing Businesses", route: "/how-to-prioritize-businesses", desc: "Filtering high-signal local businesses first" },
              { title: "Organizing Your Job Search", route: "/how-to-organize-local-job-search", desc: "Pipeline management and CRM tracking tips" },
              { title: "How to Ask If Hiring", route: "/guides/how-to-ask-if-a-business-is-hiring", desc: "Scripts for polite, effective direct inquiries" },
              { title: "Hiring Signals Guide", route: "/hiring-signals-explained", desc: "Understanding growth metrics and commercial activity" }
            ].map(item => (
              <a
                key={item.route}
                href={item.route}
                onClick={(e) => handleLinkClick(e, item.route)}
                className="p-4 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition flex flex-col justify-between space-y-2 group"
              >
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.desc}</p>
                </div>
                <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  Read Guide <ChevronRight className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* Section 6: Browse Industries */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Browse Industries
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Browse small business categories including Healthcare, Construction, Retail, Trade, and Professional Services.
              </p>
            </div>
            <a
              href="/industries"
              onClick={(e) => handleLinkClick(e, "/industries")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-cyan-50 dark:bg-cyan-950 text-cyan-800 dark:text-cyan-300 hover:bg-cyan-100 font-bold text-xs transition shrink-0"
            >
              Industry Directory <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {[
              { title: "Healthcare & Medical Practices", route: "/industries/healthcare", desc: "Dental clinics, physical therapy, medical offices & wellness practices" },
              { title: "Construction & Contracting", route: "/industries/construction", desc: "General contractors, HVAC, plumbing, electrical & trade specialists" },
              { title: "Technology & Software Firms", route: "/industries/technology", desc: "IT managed service providers, web agencies & software consultancies" },
              { title: "Retail & Local Commerce", route: "/industries/retail", desc: "Boutiques, specialty shops & local commercial storefronts" },
              { title: "Professional Services", route: "/industries/professional-services", desc: "Accounting, legal, insurance & financial advisory firms" }
            ].map(item => (
              <a
                key={item.route}
                href={item.route}
                onClick={(e) => handleLinkClick(e, item.route)}
                className="p-4 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition flex flex-col justify-between space-y-2 group"
              >
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.desc}</p>
                </div>
                <span className="text-[11px] font-semibold text-cyan-600 dark:text-cyan-400 flex items-center gap-1">
                  Explore Sector <ChevronRight className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* Section 7: Browse Features */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Browse Features
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Explore Map Discovery, Hiring Scores, Contact Finder, Website Review, and CRM Pipeline features.
              </p>
            </div>
            <a
              href="/features"
              onClick={(e) => handleLinkClick(e, "/features")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 hover:bg-purple-100 font-bold text-xs transition shrink-0"
            >
              Platform Tools <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {[
              { title: "Map Discovery Tool", route: "/features/map-discovery", desc: "Interactive geographic search with real-time business density filters" },
              { title: "Hiring Likelihood Scores", route: "/features/hiring-scores", desc: "Algorithmic hiring signals based on review surges & market activity" },
              { title: "Contact & Decision-Maker Finder", route: "/features/contact-finder", desc: "Verified business phone numbers, email domain lookups & web links" },
              { title: "CRM Outreach Pipeline", route: "/features/pipeline-crm", desc: "Track prospect stages from initial contact to interview & hiring" }
            ].map(item => (
              <a
                key={item.route}
                href={item.route}
                onClick={(e) => handleLinkClick(e, item.route)}
                className="p-4 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition flex flex-col justify-between space-y-2 group"
              >
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.desc}</p>
                </div>
                <span className="text-[11px] font-semibold text-purple-600 dark:text-purple-400 flex items-center gap-1">
                  Learn More <ChevronRight className="w-3 h-3" />
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* Section 8 & 9: Reports & Methodology */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Section 8: View Reports */}
          <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
            <div className="space-y-1 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  View Reports
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Data-driven research on small business density, hiring trends, and contactability.
              </p>
            </div>
            <div className="space-y-3 pt-1">
              <a
                href="/reports"
                onClick={(e) => handleLinkClick(e, "/reports")}
                className="block p-3.5 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition group"
              >
                <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition">
                  Market Research Reports Hub
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Explore empirical data on unadvertised job markets</p>
              </a>
            </div>
          </section>

          {/* Section 9: Learn About Our Methodology */}
          <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xs">
            <div className="space-y-1 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  Learn About Our Methodology
                </h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Mathematical breakdown of how BizNearYou calculates Hiring Likelihood and Opportunity Scores.
              </p>
            </div>
            <div className="space-y-3 pt-1">
              <a
                href="/methodology"
                onClick={(e) => handleLinkClick(e, "/methodology")}
                className="block p-3.5 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 transition group"
              >
                <h3 className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-rose-600 dark:group-hover:text-rose-400 transition">
                  Scoring & Data Science Methodology
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Learn how we score commercial activity and verification confidence</p>
              </a>
            </div>
          </section>
        </div>

      </main>
    </div>
  );
}
